import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import Layout from '../../components/Layout'
import { listingService } from '../../services/listingService'
import { PROPERTY_TYPES, PROPERTY_TYPE_LABELS, LAYOUT_OPTIONS } from '../../constants/propertyConstants'
import PropertyConditionOptions from '../../components/PropertyConditionOptions'
import ValuationWidget from '../Valuations/ValuationWidget'
import ListingMap from '../../components/ListingMap'

const API_BASE_URL = 'https://api.realitni-jestrab.cz'

const NewListing = () => {
  const navigate = useNavigate()
  const { listingId } = useParams()
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [valuationData, setValuationData] = useState(null)
  const [newListing, setNewListing] = useState({
    address: '',
    type: 'byt',
    layout: '',
    m2: '',
    condition: '',
    floor: '',
    price: '',
    description: '',
    contactInfo: '',
  })

  const isEditMode = !!listingId

  // Fetch listing data if in edit mode
  useEffect(() => {
    const fetchListing = async () => {
      if (!isEditMode) return

      try {
        setLoading(true)
        const data = await listingService.getListing(listingId)

        setNewListing({
          address: data.address || '',
          type: data.type || 'byt',
          layout: data.layout || '',
          m2: data.m2 ? data.m2.toString() : '',
          condition: data.condition || '',
          floor: data.floor || '',
          price: data.price ? data.price.toString() : '',
          description: data.description || '',
          contactInfo: data.contactInfo || '',
        })

        if (data.coordinates) {
          setCoordinates(data.coordinates)
        }
      } catch (error) {
        console.error('Error fetching listing:', error)
        setError('Chyba při načítání inzerátu')
      } finally {
        setLoading(false)
      }
    }

    fetchListing()
  }, [listingId, isEditMode])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!coordinates.lat || !coordinates.lng) {
      setError('Prosím vyberte adresu na mapě')
      return
    }

    try {
      const saveData = {
        ...newListing,
        coordinates: coordinates,
        price: newListing.price ? parseInt(newListing.price.replace(/\s/g, '')) : null,
        m2: newListing.m2 ? parseInt(newListing.m2) : null,
      }

      console.log('Saving listing:', saveData)

      let result
      if (isEditMode) {
        result = await listingService.updateListing(listingId, saveData)
        console.log('✅ Listing updated successfully:', result)
      } else {
        result = await listingService.createListing(saveData)
        console.log('✅ Listing created successfully:', result)
      }

      setError(null)
      navigate('/listings')
    } catch (error) {
      console.error('❌ Error saving listing:', error)

      let errorMessage = isEditMode ? 'Chyba při aktualizaci inzerátu' : 'Chyba při ukládání inzerátu'

      if (error.message && error.message.includes('400')) {
        try {
          const errorResponse = await error.response?.json()
          if (errorResponse && errorResponse.message) {
            errorMessage = errorResponse.message
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError)
        }
      }

      setError(errorMessage)
    }
  }

  // Create header content for the Layout
  const headerContent = (
    <div className="flex items-center gap-2 sm:gap-4">
      <button onClick={() => navigate('/listings')} className="p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md">
        <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
      </button>
      <h1 className="text-lg sm:text-2xl font-semibold text-gray-900">{isEditMode ? 'Upravit inzerát' : 'Nový inzerát'}</h1>
    </div>
  )

  if (loading) {
    return (
      <Layout headerContent={headerContent}>
        <div className="flex justify-center items-center h-64">Načítání...</div>
      </Layout>
    )
  }

  return (
    <Layout headerContent={headerContent}>
      {/* Main Content */}
      <div className="px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
          {/* Form Section */}
          <div className="space-y-4 sm:space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {loading && isEditMode && <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">Načítání dat inzerátu...</div>}
              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresa *</label>
                <input
                  type="text"
                  value={newListing.address}
                  onChange={(e) => setNewListing({ ...newListing, address: e.target.value })}
                  placeholder="Zadejte adresu"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                  required
                  disabled={loading && isEditMode}
                />
              </div>

              {/* Property Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Typ nemovitosti *</label>
                <select
                  value={newListing.type}
                  onChange={(e) => setNewListing({ ...newListing, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  {Object.entries(PROPERTY_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Layout and Size */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dispozice</label>
                  <select
                    value={newListing.layout}
                    onChange={(e) => setNewListing({ ...newListing, layout: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Vyberte dispozici</option>
                    {LAYOUT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Velikost (m²)</label>
                  <input
                    type="number"
                    value={newListing.m2}
                    onChange={(e) => setNewListing({ ...newListing, m2: e.target.value })}
                    placeholder="80"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Condition and Floor */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stav</label>
                  <select
                    value={newListing.condition}
                    onChange={(e) => setNewListing({ ...newListing, condition: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Vyberte stav</option>
                    <PropertyConditionOptions />
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Patro</label>
                  <input
                    type="text"
                    value={newListing.floor}
                    onChange={(e) => setNewListing({ ...newListing, floor: e.target.value })}
                    placeholder="2. NP"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Popis</label>
                <textarea
                  value={newListing.description}
                  onChange={(e) => setNewListing({ ...newListing, description: e.target.value })}
                  placeholder="Detailní popis nemovitosti..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Contact Info */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Kontaktní informace</label>
                <input
                  type="text"
                  value={newListing.contactInfo}
                  onChange={(e) => setNewListing({ ...newListing, contactInfo: e.target.value })}
                  placeholder="Telefon nebo email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cena (Kč)</label>
                <input
                  type="text"
                  value={newListing.price}
                  onChange={(e) => setNewListing({ ...newListing, price: e.target.value })}
                  placeholder="2 500 000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Error Message */}
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Načítání...' : isEditMode ? 'Aktualizovat inzerát' : 'Vytvořit inzerát'}
              </button>
            </form>
          </div>

          {/* Right Column - Map and Valuation */}
          <div className="space-y-4 sm:space-y-6">
            {/* Map Section */}
            <ListingMap
              coordinates={coordinates}
              address={newListing.address}
              onCoordinatesChange={setCoordinates}
              isEditMode={true}
              loading={loading}
              valuation={valuationData}
              showComparables={true}
            />

            {/* Valuation Widget */}
            <ValuationWidget
              coordinates={coordinates}
              layout={newListing.layout}
              m2={newListing.m2}
              condition={newListing.condition}
              address={newListing.address}
              onValuationDataChange={(data) => {
                console.log('✅ Valuation data changed:', data)
                setValuationData(data)
              }}
            />
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default NewListing
